import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";
import {
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  Upload,
  User,
  MapPin,
  Globe,
  MessageSquare,
  Clock,
  Home,
  Users,
  Package,
  CreditCard,
  Settings,
  FileText,
  Camera,
  Instagram,
  Link,
  Phone,
  Mail,
  Building2,
  Scissors,
  Heart,
  ExternalLink,
  Palette,
  ChefHat,
  Save,
  ArrowRight
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICON_MAP = {
  User, MapPin, Globe, MessageSquare, Clock, Home, Users,
  Package, CreditCard, Settings, FileText, Camera, Sparkles,
  Instagram, Link, Phone, Mail, Building2, Scissors, Heart, Palette, ChefHat
};

// Category theming
const CATEGORY_THEMES = {
  makeup: { 
    primary: "from-pink-500 to-rose-500",
    light: "bg-pink-50",
    text: "text-pink-600",
    border: "border-pink-200",
    bg: "bg-pink-500",
    accent: "pink"
  },
  // Aliases for makeup
  beauty: { 
    primary: "from-pink-500 to-rose-500",
    light: "bg-pink-50",
    text: "text-pink-600",
    border: "border-pink-200",
    bg: "bg-pink-500",
    accent: "pink"
  },
  mua: { 
    primary: "from-pink-500 to-rose-500",
    light: "bg-pink-50",
    text: "text-pink-600",
    border: "border-pink-200",
    bg: "bg-pink-500",
    accent: "pink"
  },
  hair: { 
    primary: "from-purple-500 to-violet-500",
    light: "bg-purple-50",
    text: "text-purple-600",
    border: "border-purple-200",
    bg: "bg-purple-500",
    accent: "purple"
  },
  nail: { 
    primary: "from-rose-400 to-pink-500",
    light: "bg-rose-50",
    text: "text-rose-600",
    border: "border-rose-200",
    bg: "bg-rose-500",
    accent: "rose"
  },
  nails: { 
    primary: "from-rose-400 to-pink-500",
    light: "bg-rose-50",
    text: "text-rose-600",
    border: "border-rose-200",
    bg: "bg-rose-500",
    accent: "rose"
  },
  lash: { 
    primary: "from-violet-500 to-purple-500",
    light: "bg-violet-50",
    text: "text-violet-600",
    border: "border-violet-200",
    bg: "bg-violet-500",
    accent: "violet"
  },
  lashes: { 
    primary: "from-violet-500 to-purple-500",
    light: "bg-violet-50",
    text: "text-violet-600",
    border: "border-violet-200",
    bg: "bg-violet-500",
    accent: "violet"
  },
  culinary: { 
    primary: "from-orange-500 to-amber-500",
    light: "bg-orange-50",
    text: "text-orange-600",
    border: "border-orange-200",
    bg: "bg-orange-500",
    accent: "orange"
  },
  chef: { 
    primary: "from-orange-500 to-amber-500",
    light: "bg-orange-50",
    text: "text-orange-600",
    border: "border-orange-200",
    bg: "bg-orange-500",
    accent: "orange"
  },
  instructor: { 
    primary: "from-blue-500 to-sky-500",
    light: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-200",
    bg: "bg-blue-500",
    accent: "blue"
  },
  event: { 
    primary: "from-indigo-500 to-purple-500",
    light: "bg-indigo-50",
    text: "text-indigo-600",
    border: "border-indigo-200",
    bg: "bg-indigo-500",
    accent: "indigo"
  },
  events: { 
    primary: "from-indigo-500 to-purple-500",
    light: "bg-indigo-50",
    text: "text-indigo-600",
    border: "border-indigo-200",
    bg: "bg-indigo-500",
    accent: "indigo"
  },
  wellness: { 
    primary: "from-green-500 to-emerald-500",
    light: "bg-green-50",
    text: "text-green-600",
    border: "border-green-200",
    bg: "bg-green-500",
    accent: "green"
  },
  default: { 
    primary: "from-pink-500 to-rose-500",
    light: "bg-pink-50",
    text: "text-pink-600",
    border: "border-pink-200",
    bg: "bg-pink-500",
    accent: "pink"
  }
};

// Default intake sections if backend has no template - MATCHES intake_templates.py
const DEFAULT_SECTIONS = [
  {
    id: "section_your_info",
    title: "Your Info",
    subtitle: "Tell us about yourself",
    icon: "User",
    questions: [
      { id: "q_business_name", type: "text", label: "Business Name", required: true, placeholder: "Your business or brand name" },
      { id: "q_location", type: "text", label: "Location (City)", required: true, placeholder: "Where are you based?" },
      { id: "q_first_name", type: "text", label: "First Name", required: true, placeholder: "Your first name" },
      { id: "q_last_name", type: "text", label: "Last Name", required: true, placeholder: "Your last name" },
      { id: "q_gender", type: "select", label: "Gender", required: false, options: ["Woman", "Man", "Non-binary", "I prefer not to say"] },
      { id: "q_race", type: "select", label: "Race/Ethnicity", required: false, options: ["Asian", "Black", "Hispanic/Latino", "Middle Eastern", "White", "Mixed/Multiple", "Other", "I prefer not to say"] }
    ]
  },
  {
    id: "section_discovery",
    title: "Where Clients Find You",
    subtitle: "Your online presence",
    icon: "Globe",
    questions: [
      { id: "q_instagram", type: "text", label: "Instagram", placeholder: "@yourhandle" },
      { id: "q_tiktok", type: "text", label: "TikTok", placeholder: "@yourhandle" },
      { id: "q_facebook", type: "text", label: "Facebook", placeholder: "Your page name or URL" },
      { id: "q_website", type: "text", label: "Website", placeholder: "yoursite.com" },
      { id: "q_google_business", type: "text", label: "Google Business", placeholder: "Your business profile" },
      { id: "q_word_of_mouth", type: "multiselect", label: "Word of Mouth", has_other: true, options: ["Friends/Family referrals", "Existing client referrals", "Community events", "Professional networks"] },
      { id: "q_other_discovery", type: "text", label: "Other", placeholder: "Any other platforms" },
      { id: "q_discovery_channels", type: "multiselect", label: "Where do clients find you?", required: true, has_other: true, options: ["Instagram", "TikTok", "Facebook", "Website", "Google Business", "Word of Mouth", "Yelp", "StyleSeat", "Booksy"] },
      { id: "q_primary_discovery", type: "multiselect", label: "Where do MOST clients find you?", required: true, has_other: true, options: ["Instagram", "TikTok", "Google Business", "Word of Mouth", "Referrals"] },
      { id: "q_booking_method", type: "multiselect", label: "Primary Booking Method", required: true, has_other: true, options: ["DM/Text", "Phone Call", "Booking Platform", "Walk-ins", "Email"] },
      { id: "q_booking_platform", type: "multiselect", label: "Which booking platform do you use?", has_other: true, options: ["StyleSeat", "GlossGenius", "Booksy", "Vagaro", "Square Appointments", "Acuity", "Calendly", "Website booking"] }
    ]
  },
  {
    id: "section_booking",
    title: "How Clients Book With You",
    subtitle: "Your booking process",
    icon: "Clock",
    questions: [
      { id: "q_response_time", type: "multiselect", label: "How quickly do you respond?", required: true, options: ["Within an hour", "Same day", "Within 24 hours", "Within 48 hours", "When I can"] },
      { id: "q_confirmation_method", type: "multiselect", label: "How do you confirm appointments?", has_other: true, options: ["Text message", "Email", "Phone call", "Platform notification", "DM"] },
      { id: "q_deposit_required", type: "yesno", label: "Do you require deposits?" }
    ]
  },
  {
    id: "section_service_day",
    title: "Service Day",
    subtitle: "What happens on appointment day",
    icon: "Home",
    questions: [
      { id: "q_service_location", type: "multiselect", label: "Where do you provide services?", required: true, has_other: true, options: ["My home/studio", "I travel to clients", "Outdoor/Events", "Salon/Spa", "Rented space/booth"] },
      { id: "q_client_prep", type: "multiselect", label: "What should clients do before arriving?", has_other: true, options: ["Wash hair/nails", "No product in hair", "Bring reference photos", "Eat beforehand", "Arrive with clean face", "Nothing specific"] },
      { id: "q_client_bring", type: "textarea", label: "Anything clients need to bring?", placeholder: "e.g., Reference photos, hair products, nothing needed..." }
    ]
  },
  {
    id: "section_relationships",
    title: "Building Relationships",
    subtitle: "How do you keep clients coming back?",
    icon: "Heart",
    questions: [
      { id: "q_followup", type: "multiselect", label: "How do you follow up after appointments?", has_other: true, options: ["Text/DM check-in", "Send photos of their look", "Rebook reminder", "Birthday messages", "I don't follow up"] },
      { id: "q_rebooking", type: "multiselect", label: "How do clients typically rebook?", options: ["They reach out when ready", "I remind them", "Book next appointment before leaving", "Subscription/regular schedule"] },
      { id: "q_retention_notes", type: "textarea", label: "Anything else about client relationships?", placeholder: "What makes clients come back?" },
      { id: "q_common_questions", type: "textarea", label: "Questions you repeat to every client?", placeholder: "e.g., What's your hair texture? Any allergies?" },
      { id: "q_inspo_method", type: "multiselect", label: "How do clients share inspiration?", has_other: true, options: ["Screenshots in DM", "Pinterest boards", "Show me on their phone", "Describe verbally", "They don't share inspo"] }
    ]
  },
  {
    id: "section_business",
    title: "Your Business & Preferences",
    subtitle: "Tell us about your setup",
    icon: "Settings",
    questions: [
      { id: "q_logo_upload", type: "file", label: "Upload your business logo" },
      { id: "q_client_info", type: "multiselect", label: "What do you need to know about every client?", has_other: true, options: ["Allergies", "Sensitivities", "Preferred scents", "Previous treatments", "Skin type", "Hair texture", "Dietary restrictions", "Style preferences"] },
      { id: "q_brands", type: "textarea", label: "Brands/products you use", placeholder: "e.g., Olaplex, Redken, MAC..." },
      { id: "q_sell_products", type: "yesno", label: "Do you sell products to clients?" },
      { id: "q_prep_routine", type: "textarea", label: "How do you prepare before clients?", placeholder: "e.g., Review preferences, set up station..." },
      { id: "q_review_history", type: "yesno", label: "Do you review client history before appointments?" },
      { id: "q_specialties", type: "textarea", label: "Your specialties or signature services", placeholder: "What are you known for?" },
      { id: "q_certifications", type: "textarea", label: "Certifications & training", placeholder: "List any certifications..." }
    ]
  },
  {
    id: "section_operations",
    title: "Business Operations",
    subtitle: "Policies and payments",
    icon: "Briefcase",
    questions: [
      { id: "q_cancellation_policy", type: "textarea", label: "Your cancellation/no-show policy", placeholder: "e.g., 24-hour notice required..." },
      { id: "q_packages", type: "yesno", label: "Do you offer packages or memberships?" },
      { id: "q_payment_methods", type: "multiselect", label: "Payment methods you accept", has_other: true, options: ["Venmo", "Cash App", "Zelle", "Square", "Stripe", "PayPal", "Cash", "Card Reader"] }
    ]
  },
  {
    id: "section_preferences",
    title: "Portal Preferences",
    subtitle: "Customize your client portal",
    icon: "Sliders",
    questions: [
      { id: "q_collect_preferences", type: "toggle", label: "Collect client preferences", description: "Capture style, allergies, etc." },
      { id: "q_send_reminders", type: "toggle", label: "Send appointment reminders", description: "Automatic day-before reminders" },
      { id: "q_show_prices", type: "toggle", label: "Show prices on your portal", description: "Display your pricing" },
      { id: "q_require_deposit", type: "toggle", label: "Require deposits for bookings", description: "Ask for payment upfront" }
    ]
  },
  {
    id: "section_final",
    title: "Anything Else",
    subtitle: "Share anything we missed",
    icon: "MessageCircle",
    questions: [
      { id: "q_anything_else", type: "textarea", label: "Anything else to share?", placeholder: "e.g., I specialize in natural textures... My clients love the home studio vibe..." }
    ]
  }
];

// Tab navigation component matching the PDF design
function SectionTabs({ sections, currentIndex, onSelect, theme }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2 scrollbar-hide">
      {sections.map((section, idx) => {
        const isActive = idx === currentIndex;
        const isCompleted = idx < currentIndex;
        return (
          <button
            key={section.id}
            onClick={() => onSelect(idx)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-lg transition-all ${
              isActive 
                ? `text-white shadow-md` 
                : isCompleted
                  ? `text-gray-700`
                  : "text-gray-500 hover:bg-gray-100"
            }`}
            style={
              isActive
                ? { backgroundColor: theme.inlineDark || undefined }
                : isCompleted && theme.inlineLight
                  ? { backgroundColor: theme.inlineLight, color: theme.inlineDark }
                  : undefined
            }
            data-testid={`section-tab-${section.id}`}
          >
            {section.title}
          </button>
        );
      })}
    </div>
  );
}

export default function ModernIntakePage() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get("edit") === "true";
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [responses, setResponses] = useState({});
  const [otherValues, setOtherValues] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [categoryType, setCategoryType] = useState("makeup");
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [portalData, setPortalData] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // Canonical category branding from API (admin-controlled, source of truth)
  const [categoryBranding, setCategoryBranding] = useState({ dark_hex: "", light_hex: "", icon_url: "" });

  // Theme: prefer canonical hex from `categories` collection; Tailwind palette is purely structural fallback.
  const tailwindFallback = CATEGORY_THEMES[categoryType] || CATEGORY_THEMES.default;
  const darkHex = categoryBranding.dark_hex || "";
  const lightHex = categoryBranding.light_hex || "";
  const theme = {
    ...tailwindFallback,
    // When admin hex is present, the page uses it as a CSS variable for ALL accent surfaces.
    inlineDark: darkHex,
    inlineLight: lightHex,
  };
  
  // Fetch template and existing data
  useEffect(() => {
    loadData();
  }, [portalId]);
  
  const loadData = async () => {
    try {
      const res = await fetch(`${API}/api/pilot/portal/${portalId}/intake-template`);
      if (res.ok) {
        const data = await res.json();
        setPortalData(data);
        
        const catType = (data.category_type || "makeup").toLowerCase();
        setCategoryType(catType);
        
        // Capture canonical category branding (dark_hex/light_hex/icon_url) from admin DB
        if (data.category_branding) {
          setCategoryBranding({
            dark_hex: data.category_branding.dark_hex || data.primary_color || "",
            light_hex: data.category_branding.light_hex || data.light_color || "",
            icon_url: data.category_branding.icon_url || data.icon_url || "",
          });
        } else if (data.primary_color) {
          setCategoryBranding({
            dark_hex: data.primary_color,
            light_hex: data.light_color || "",
            icon_url: data.icon_url || "",
          });
        }
        
        // Use backend sections if available (new format)
        if (data.sections && data.sections.length > 0) {
          // Convert all "select" types to "multiselect" as per user request
          const processedSections = data.sections.map(section => ({
            ...section,
            questions: section.questions.map(q => ({
              ...q,
              type: q.type === "select" ? "multiselect" : q.type,
              has_other: q.has_other !== false // Default to true for multiselect
            }))
          }));
          setSections(processedSections);
        } else if (data.questions && data.questions.length > 0) {
          // Fallback: Convert flat questions array into sections
          const templateSections = convertQuestionsToSections(data.questions, catType);
          setSections(templateSections);
        }
        // If neither, keep DEFAULT_SECTIONS
        
        // Merge prefill and existing responses
        // Map prefill keys to question IDs (prefill uses first_name, questions use q_first_name)
        const mappedPrefill = {};
        if (data.prefill) {
          Object.entries(data.prefill).forEach(([key, value]) => {
            if (value) {
              // Store both with and without q_ prefix
              mappedPrefill[key] = value;
              mappedPrefill[`q_${key}`] = value;
            }
          });
          // Special mapping: location_city -> q_location
          if (data.prefill.location_city) {
            mappedPrefill["q_location"] = data.prefill.location_city;
          }
          if (data.prefill.city) {
            mappedPrefill["q_location"] = mappedPrefill["q_location"] || data.prefill.city;
          }
          // Special mapping: most_clients -> q_primary_discovery (template question ID mismatch)
          if (data.prefill.most_clients) {
            mappedPrefill["q_primary_discovery"] = data.prefill.most_clients;
          }
        }
        const initialResponses = { 
          ...mappedPrefill, 
          ...data.existing_responses,
        };
        setResponses(initialResponses);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      toast.error("Failed to load intake form");
    } finally {
      setLoading(false);
    }
  };
  
  // Convert flat questions array from backend to sections
  const convertQuestionsToSections = (questions, catType) => {
    // Group questions by their section if specified, otherwise use defaults
    const sectionGroups = {};
    
    questions.forEach(q => {
      const sectionId = q.section || "general";
      if (!sectionGroups[sectionId]) {
        sectionGroups[sectionId] = [];
      }
      // Convert all "select" types to "multiselect" as per user request
      const convertedQuestion = {
        ...q,
        type: q.type === "select" ? "multiselect" : q.type,
        has_other: q.has_other !== false // Default to true for multiselect
      };
      sectionGroups[sectionId].push(convertedQuestion);
    });
    
    // If we have grouped sections, use them
    if (Object.keys(sectionGroups).length > 0 && !sectionGroups.general) {
      return Object.entries(sectionGroups).map(([id, questions], idx) => ({
        id,
        title: id.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
        icon: idx === 0 ? "User" : idx === 1 ? "Globe" : "Settings",
        questions
      }));
    }
    
    // Otherwise return default sections with the questions distributed
    return DEFAULT_SECTIONS;
  };
  
  const currentSectionData = sections[currentSection];
  const totalSections = sections.length;
  const progress = totalSections > 0 ? ((currentSection + 1) / totalSections) * 100 : 0;
  
  // Get section icon
  const SectionIcon = ICON_MAP[currentSectionData?.icon] || Sparkles;
  
  // Response handlers
  const handleResponseChange = useCallback((questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  }, []);
  
  const handleMultiselectToggle = useCallback((questionId, option) => {
    setResponses(prev => {
      const current = prev[questionId] || [];
      const optionValue = typeof option === "object" ? option.value : option;
      const newValue = current.includes(optionValue)
        ? current.filter(v => v !== optionValue)
        : [...current, optionValue];
      return { ...prev, [questionId]: newValue };
    });
  }, []);
  
  const handleOtherChange = useCallback((questionId, value) => {
    setOtherValues(prev => ({ ...prev, [questionId]: value }));
    setResponses(prev => ({ ...prev, [`${questionId}_other`]: value }));
  }, []);
  
  // Logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "pilot-intake");
      
      // Use the pilot-specific upload endpoint (no auth required)
      const res = await fetch(`${API}/api/pilot/upload-image`, {
        method: "POST",
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        // R6 - write to the question id so the dropzone preview renders.
        handleResponseChange("q_logo_upload", data.url);
        toast.success("Logo uploaded!");
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || "Upload failed");
      }
    } catch (err) {
      toast.error("Error uploading logo");
    } finally {
      setUploadingLogo(false);
    }
  };
  
  // Validation
  const validateCurrentSection = () => {
    if (!currentSectionData) return true;
    
    for (const q of currentSectionData.questions) {
      if (q.required) {
        const value = responses[q.id];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          toast.error(`Please answer: ${q.label}`);
          return false;
        }
      }
    }
    return true;
  };
  
  // Navigation
  const handleNext = () => {
    if (!validateCurrentSection()) return;
    if (currentSection < totalSections - 1) {
      setCurrentSection(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  
  const handlePrev = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  
  // Submit
  const handleSubmit = async () => {
    if (!validateCurrentSection()) return;
    
    setSaving(true);
    try {
      const allResponses = { 
        ...responses, 
        ...Object.fromEntries(
          Object.entries(otherValues).map(([k, v]) => [`${k}_other`, v])
        )
      };
      
      const res = await fetch(`${API}/api/pilot/portal/${portalId}/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          responses: allResponses,
          is_complete: true,
          stage: totalSections
        })
      });
      
      if (res.ok) {
        toast.success("Setup complete! 🎉");
        navigate(`/pilot/confirmation/${portalId}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (err) {
      toast.error("Error saving");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };
  
  // Render individual question
  const renderQuestion = (question) => {
    const value = responses[question.id];
    
    switch (question.type) {
      case "text":
      case "email":
      case "phone":
        return (
          <Input
            type={question.type === "email" ? "email" : question.type === "phone" ? "tel" : "text"}
            value={value || ""}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            className="mt-2 h-12 text-base border-gray-200 focus:border-gray-400 focus:ring-0 rounded-xl"
            data-testid={`intake-${question.id}`}
          />
        );
        
      case "textarea":
        return (
          <div className="mt-2">
            {question.description && (
              <p className="text-sm text-gray-500 mb-2">{question.description}</p>
            )}
            <Textarea
              value={value || ""}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              rows={3}
              className="text-base border-gray-200 focus:border-gray-400 focus:ring-0 rounded-xl resize-none"
              data-testid={`intake-${question.id}`}
            />
          </div>
        );
        
      case "multiselect":
        return (
          <div className="mt-3">
            {question.subtitle && (
              <p className="text-sm text-gray-500 mb-3">{question.subtitle}</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(question.options || []).map((opt, idx) => {
                const optValue = typeof opt === "object" ? opt.value : opt;
                const optLabel = typeof opt === "object" ? opt.label : opt;
                const optDesc = typeof opt === "object" ? opt.desc : null;
                const isSelected = (value || []).includes(optValue);
                
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleMultiselectToggle(question.id, opt)}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? (theme.inlineLight ? "" : `${theme.light} ${theme.border} ${theme.text}`)
                        : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                    style={
                      isSelected && theme.inlineLight
                        ? { backgroundColor: theme.inlineLight, borderColor: theme.inlineDark, color: theme.inlineDark }
                        : undefined
                    }
                    data-testid={`intake-${question.id}-${optValue}`}
                  >
                    <div
                      className={`w-5 h-5 rounded-md border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                        isSelected ? (theme.inlineDark ? "" : `${theme.border} ${theme.bg}`) : "border-gray-300"
                      }`}
                      style={
                        isSelected && theme.inlineDark
                          ? { backgroundColor: theme.inlineDark, borderColor: theme.inlineDark }
                          : undefined
                      }
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span
                        className={`block font-medium ${isSelected && !theme.inlineDark ? theme.text : ""}`}
                        style={isSelected && theme.inlineDark ? { color: theme.inlineDark } : (isSelected ? undefined : { color: "#111827" })}
                      >
                        {optLabel}
                      </span>
                      {optDesc && (
                        <span className="block text-sm text-gray-500 mt-0.5">{optDesc}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Other option */}
            {question.has_other && (
              <div className="mt-4">
                <Label className="text-sm text-gray-600 mb-2 block">Other (please specify)</Label>
                <Input
                  placeholder="Type here..."
                  value={otherValues[question.id] || ""}
                  onChange={(e) => handleOtherChange(question.id, e.target.value)}
                  className="h-11 rounded-xl border-gray-200"
                />
              </div>
            )}
          </div>
        );
        
      case "yesno":
        return (
          <div className="mt-3">
            {question.description && (
              <p className="text-sm text-gray-500 mb-3">{question.description}</p>
            )}
            <div className="flex gap-3">
              {["Yes", "No"].map((opt) => {
                const isSelected = value === opt.toLowerCase();
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handleResponseChange(question.id, opt.toLowerCase())}
                    className={`flex-1 py-3 px-6 rounded-xl border-2 font-medium transition-all ${
                      isSelected
                        ? (theme.inlineLight ? "" : `${theme.light} ${theme.border} ${theme.text}`)
                        : "bg-white border-gray-200 hover:border-gray-300 text-gray-700"
                    }`}
                    style={
                      isSelected && theme.inlineLight
                        ? { backgroundColor: theme.inlineLight, borderColor: theme.inlineDark, color: theme.inlineDark }
                        : undefined
                    }
                    data-testid={`intake-${question.id}-${opt.toLowerCase()}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        );
        
      case "toggle":
        return (
          <div className="mt-2 flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-white">
            <div>
              <p className="font-medium text-gray-900">{question.label}</p>
              {question.description && (
                <p className="text-sm text-gray-500 mt-0.5">{question.description}</p>
              )}
            </div>
            <Switch
              checked={value || false}
              onCheckedChange={(checked) => handleResponseChange(question.id, checked)}
              data-testid={`intake-${question.id}`}
            />
          </div>
        );
        
      case "file":
        return (
          <div className="mt-3">
            {question.description && (
              <p className="text-sm text-gray-500 mb-3">{question.description}</p>
            )}
            {value ? (
              <div className="relative w-32 h-32 rounded-xl border-2 border-gray-200 overflow-hidden bg-gray-50">
                <img src={value} alt="Logo" className="w-full h-full object-contain p-2" />
                <button
                  onClick={() => handleResponseChange(question.id, null)}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  ×
                </button>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                />
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:bg-gray-50 hover:border-gray-400 transition-all">
                  {uploadingLogo ? (
                    <RefreshCw className="w-8 h-8 mx-auto text-gray-400 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 font-medium">Drag and drop your logo, or click to browse</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, or SVG • Max 2MB</p>
                    </>
                  )}
                </div>
              </label>
            )}
          </div>
        );
        
      default:
        return (
          <Input
            value={value || ""}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            className="mt-2 h-12 rounded-xl border-gray-200"
          />
        );
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Loading your setup...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <PageMeta 
        title={`${portalData?.portal_name || categoryType?.charAt(0).toUpperCase() + categoryType?.slice(1) || 'Pilot'} Intake Form`}
        description={`Complete your intake form for ${portalData?.portal_name || categoryType}`}
      />
      {/* Header */}
      <header
        className={`sticky top-0 z-50 ${theme.inlineLight ? "" : theme.light} border-b backdrop-blur-sm bg-opacity-95`}
        style={theme.inlineLight ? { backgroundColor: theme.inlineLight } : undefined}
      >
        <div className="max-w-3xl mx-auto px-4 py-4">
          {/* Logo and title */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl ${theme.inlineDark ? "" : `bg-gradient-to-br ${theme.primary}`} flex items-center justify-center shadow-md overflow-hidden`}
                style={theme.inlineDark ? { backgroundColor: theme.inlineDark } : undefined}
              >
                {categoryBranding.icon_url ? (
                  <img src={categoryBranding.icon_url} alt={categoryType} className="w-6 h-6 object-contain" />
                ) : (
                  <span className="text-white font-bold text-lg">P</span>
                )}
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Your Setup</h1>
                <p className="text-sm text-gray-500">Configure your portal</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {currentSection + 1} of {totalSections}
              </Badge>
              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                <Check className="w-3 h-3" /> Progress saved!
              </span>
            </div>
          </div>
          
          {/* Section tabs */}
          <SectionTabs 
            sections={sections} 
            currentIndex={currentSection} 
            onSelect={setCurrentSection}
            theme={theme}
          />
        </div>
      </header>
      
      {/* Progress bar */}
      <div className="max-w-3xl mx-auto px-4">
        <Progress value={progress} className="h-1 mt-0" />
      </div>
      
      {/* Main content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Section header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div
              className={`w-12 h-12 rounded-2xl ${theme.inlineDark ? "" : `bg-gradient-to-br ${theme.primary}`} flex items-center justify-center shadow-lg overflow-hidden`}
              style={theme.inlineDark ? { backgroundColor: theme.inlineDark } : undefined}
            >
              {categoryBranding.icon_url ? (
                <img src={categoryBranding.icon_url} alt={categoryType} className="w-7 h-7 object-contain" />
              ) : (
                <SectionIcon className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{currentSectionData?.title}</h2>
              {currentSectionData?.subtitle && (
                <p className="text-gray-500">{currentSectionData.subtitle}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Questions */}
        <div className="space-y-8">
          {currentSectionData?.questions.map((question) => (
            <div key={question.id} className="space-y-1">
              {question.type !== "toggle" && (
                <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  {question.label}
                  {question.required && <span className="text-red-500">*</span>}
                </Label>
              )}
              {renderQuestion(question)}
            </div>
          ))}
        </div>
        
        {/* Navigation buttons */}
        <div className="flex items-center justify-between mt-12 pt-8 border-t">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentSection === 0}
            className="h-12 px-6 rounded-xl"
            data-testid="intake-prev-btn"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="h-12 px-4 rounded-xl text-gray-600"
              onClick={() => toast.info("Link copied!")}
            >
              <Save className="w-4 h-4 mr-2" />
              Save & Copy Link
            </Button>
            
            {currentSection < totalSections - 1 ? (
              <Button
                onClick={handleNext}
                className={`h-12 px-8 rounded-xl ${theme.inlineDark ? "text-white" : `bg-gradient-to-r ${theme.primary}`} hover:opacity-90 transition-opacity shadow-lg`}
                style={theme.inlineDark ? { backgroundColor: theme.inlineDark } : undefined}
                data-testid="intake-next-btn"
              >
                Continue
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="h-12 px-8 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 transition-opacity shadow-lg"
                data-testid="intake-submit-btn"
              >
                {saving ? (
                  <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Check className="w-5 h-5 mr-2" />
                )}
                Submit & Finish
              </Button>
            )}
          </div>
        </div>
        
        {/* Completion message for last section */}
        {currentSection === totalSections - 1 && (
          <div
            className={`mt-8 rounded-2xl p-6 border ${theme.inlineLight ? "" : `${theme.light} ${theme.border}`}`}
            style={theme.inlineLight ? { backgroundColor: theme.inlineLight, borderColor: theme.inlineDark } : undefined}
          >
            <div className="flex items-start gap-4">
              <div
                className={`w-12 h-12 rounded-xl ${theme.inlineDark ? "" : `bg-gradient-to-br ${theme.primary}`} flex items-center justify-center flex-shrink-0 overflow-hidden`}
                style={theme.inlineDark ? { backgroundColor: theme.inlineDark } : undefined}
              >
                {categoryBranding.icon_url ? (
                  <img src={categoryBranding.icon_url} alt={categoryType} className="w-7 h-7 object-contain" />
                ) : (
                  <Sparkles className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">You're Almost There!</h3>
                <p className="text-gray-600 mt-1">
                  As a pilot partner, you'll get lifetime Luxe access-on us. 
                  Complete your setup to unlock your personalized portal.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
